import React, { useState, useEffect, useRef } from 'react';
import './Footer.css';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../LanguagePrefix';
import { useLocation, useNavigate } from 'react-router-dom';
import { setLanguage } from '../i18n';

const SUPPORTED = ['en', 'ro', 'es', 'de'];
const API = 'https://api.prostay.com';

function Footer() {
  const langPrefix = useLanguagePrefix();
  const navigate = useNavigate();
  const location = useLocation();
  const [language, setLanguageState] = useState('en');
  const { i18n, t } = useTranslation();

  const [hrefByLang, setHrefByLang] = useState({ en: '#', es: '#', ro: '#', de: '#' });

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Espanol' },
    // { value: 'ro', label: 'Romana' },
    // { value: 'de', label: 'Deutsch' },
  ];

  useEffect(() => {
    const languageMap = { en: 'en', es: 'es', ro: 'ro', de: 'de' };
    setLanguageState(languageMap[i18n.language] || 'en');
  }, [i18n.language]);

  const ensureTrailing = (p) => (p === '/' ? '/' : (p.endsWith('/') ? p : p + '/'));
  const stripLang = (pathname) => {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length && SUPPORTED.includes(parts[0])) parts.shift();
    const clean = '/' + parts.join('/');
    const hadTrailing = pathname.length > 1 && pathname.endsWith('/');
    return hadTrailing ? ensureTrailing(clean || '/') : (clean || '/');
  };

  const fetchArticleBySlug = async (slug) => {
    try {
      const res = await fetch(`${API}/WebsiteKnowledgebase/get-article-by-slug/${encodeURIComponent(slug)}`);
      if (!res.ok) throw new Error('not ok');
      return await res.json(); // { slug, slugTranslations?: { [lang]: string }, ... }
    } catch (e) {
      console.error('slug fetch failed', e);
      return null;
    }
  };

  // ⭐ Correct slug resolver (fix): English uses article.slug (default)
  const getTargetSlug = (lang, articleData, currentSlug) => {
    if (!articleData) return currentSlug;
    if (lang === 'en') {
      return articleData.slug || articleData.slugTranslations?.en || currentSlug;
    }
    return articleData.slugTranslations?.[lang] || currentSlug;
  };

  const buildHrefFor = (lang, basePath, qs, hash, articleData, context) => {
    let path = basePath;

    if (context?.type === 'blog') {
      const targetSlug = getTargetSlug(lang, articleData, context.currSlug);
      path = `/blog/${targetSlug}${context.pageSeg || ''}/`;
    } else if (context?.type === 'resources') {
      const targetSlug = getTargetSlug(lang, articleData, context.currSlug);
      path = `/resources/${context.cat}/${context.subcat}/${targetSlug}/`;
    } else {
      path = ensureTrailing(basePath);
    }

    const localized = lang === 'en' ? path : `/${lang}${path}`;
    return `${localized}${qs}${hash}`;
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const base = stripLang(location.pathname);
      const qs = location.search || '';
      const hash = location.hash || '';

      let context = { type: null };
      const blogMatch = base.match(/^\/blog\/([^/]+)(?:\/page\/(\d+))?\/?$/i);
      const resMatch = base.match(/^\/resources\/([^/]+)\/([^/]+)\/([^/]+)\/?$/i);

      let articleData = null;

      if (blogMatch) {
        context = { type: 'blog', currSlug: blogMatch[1], pageSeg: blogMatch[2] ? `/page/${blogMatch[2]}` : '' };
        articleData = await fetchArticleBySlug(context.currSlug);
      } else if (resMatch) {
        context = { type: 'resources', cat: resMatch[1], subcat: resMatch[2], currSlug: resMatch[3] };
        articleData = await fetchArticleBySlug(context.currSlug);
      }

      const next = {};
      SUPPORTED.forEach((lang) => {
        next[lang] = buildHrefFor(lang, base, qs, hash, articleData, context);
      });

      if (!cancelled) setHrefByLang(next);
    })();

    return () => { cancelled = true; };
  }, [location.pathname, location.search, location.hash]);

  const handleLanguageChange = async (lang, e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      await setLanguage(lang);
    } finally {
      const dest = hrefByLang[lang] || '#';
      navigate(dest, { replace: true });
      handleClose();
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const handleToggle = () => setIsOpen((prev) => !prev);
  const handleClose = () => setIsOpen(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        handleClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className='footer'>
      <div className='d-flex justify-content-center'>
        <div className='footer-grid page-content'>
          <div className='col'>
            <svg width="102" height="26" viewBox="0 0 102 26" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.438361 25.5C0.156558 25.5 0 25.3148 0 25.037V4.97531C0 4.69753 0.156558 4.51235 0.438361 4.51235H2.7241C3.00591 4.51235 3.16246 4.69753 3.16246 4.97531L3.22509 7.01235C4.28968 5.25309 6.10575 4.14197 8.64198 4.14197C13.0256 4.14197 16.0315 7.59876 16.0315 12.0741C16.0315 16.5802 13.0256 20.0062 8.64198 20.0062C6.2623 20.0062 4.47755 19.0185 3.38164 17.3827V25.037C3.38164 25.3148 3.22509 25.5 2.94328 25.5H0.438361ZM3.35033 12.1049C3.35033 15.0062 5.26034 16.9506 7.98444 16.9506C10.7399 16.9506 12.7438 15.0062 12.7438 12.0741C12.7438 9.17284 10.7399 7.19753 7.98444 7.19753C5.26034 7.19753 3.35033 9.17284 3.35033 12.1049Z" fill="#22242A" />
              <path d="M18.3202 19.6358C18.0384 19.6358 17.8818 19.4506 17.8818 19.1728V4.97531C17.8818 4.69753 18.0384 4.51235 18.3202 4.51235H20.6999C20.9817 4.51235 21.1382 4.6358 21.1382 5.00617L21.2321 6.82716C21.7958 5.31481 22.7977 4.14197 24.6451 4.14197C25.334 4.14197 25.8036 4.2963 26.0541 4.48148C26.2733 4.6358 26.3359 4.79012 26.3359 5.0679V7.04321C26.3359 7.38272 26.1167 7.47531 25.741 7.38272C25.3653 7.25926 25.0208 7.22839 24.6138 7.22839C22.2028 7.22839 21.2635 9.08025 21.2635 11.0247V19.1728C21.2635 19.4506 21.1069 19.6358 20.8251 19.6358H18.3202Z" fill="#22242A" />
              <path d="M34.5251 20.0062C29.9536 20.0062 26.572 16.5494 26.572 12.0741C26.572 7.59876 29.9536 4.14197 34.5251 4.14197C39.0653 4.14197 42.4469 7.59876 42.4469 12.0741C42.4469 16.5494 39.0653 20.0062 34.5251 20.0062ZM29.8284 12.0741C29.8284 14.9753 31.8636 16.9198 34.5251 16.9198C37.1553 16.9198 39.1905 14.9753 39.1905 12.0741C39.1905 9.17284 37.1553 7.22839 34.5251 7.22839C31.8636 7.22839 29.8284 9.17284 29.8284 12.0741Z" fill="#22242A" />
              <path d="M48.8675 20.0062C46.3626 20.0062 44.5465 18.8642 43.4819 17.5988C43.294 17.3827 43.294 17.0741 43.5132 16.7654L44.3273 15.6543C44.5465 15.3457 44.8283 15.3765 45.0788 15.5926C46.206 16.5494 47.3019 17.1667 48.7109 17.1667C50.0886 17.1667 51.0593 16.6111 51.0593 15.4074C51.0593 14.358 50.3391 13.8642 49.0867 13.3395L47.208 12.5988C45.2667 11.8272 43.795 10.5926 43.795 8.46296C43.795 5.74691 46.1434 4.14197 48.8675 4.14197C50.7462 4.14197 52.4996 4.91358 53.6895 6.3642C53.8773 6.58025 53.846 6.82716 53.6582 7.01235L52.6875 8.15432C52.437 8.4321 52.2178 8.40123 51.936 8.15432C50.9654 7.25926 50.026 6.82716 49.0241 6.82716C47.8342 6.82716 47.0201 7.41358 47.0201 8.4321C47.0201 9.48148 47.8342 9.94444 49.0241 10.4074L50.8714 11.1481C52.8441 11.9198 54.4096 13.1852 54.4096 15.5C54.3783 18.463 51.8108 20.0062 48.8675 20.0062Z" fill="#22242A" />
              <path d="M61.2593 19.821C58.9735 19.821 57.2514 18.7407 57.2514 15.9321V7.16667H55.2788C54.997 7.16667 54.8404 6.98148 54.8404 6.7037V4.97531C54.8404 4.69753 54.997 4.51235 55.2788 4.51235H57.2514V0.962963C57.2514 0.685185 57.408 0.5 57.6898 0.5H60.226C60.5078 0.5 60.6644 0.685185 60.6644 0.962963V4.51235H63.138C63.4198 4.51235 63.6076 4.69753 63.6076 4.97531V6.7037C63.6076 6.98148 63.4198 7.16667 63.138 7.16667H60.6644V15.5309C60.6644 16.5494 61.1653 16.9815 62.0421 16.9815C62.2926 16.9815 62.6683 16.9815 63.0127 16.9506C63.3885 16.8889 63.5763 17.0432 63.5763 17.3519V19.0185C63.5763 19.6358 62.1986 19.821 61.2593 19.821Z" fill="#22242A" />
              <path d="M71.7371 20.0062C67.3535 20.0062 64.3476 16.5802 64.3476 12.0741C64.3476 7.59876 67.3535 4.14197 71.7371 4.14197C74.2734 4.14197 76.0894 5.25309 77.154 7.01235L77.2167 4.97531C77.2167 4.69753 77.3732 4.51235 77.655 4.51235H79.9408C80.2226 4.51235 80.3791 4.69753 80.3791 4.97531V19.1728C80.3791 19.4506 80.2226 19.6358 79.9408 19.6358H77.655C77.3732 19.6358 77.2167 19.4506 77.2167 19.1728L77.154 17.1667C76.0581 18.9259 74.2421 20.0062 71.7371 20.0062ZM67.6353 12.0741C67.6353 15.0062 69.6393 16.9506 72.3947 16.9506C75.1188 16.9506 77.0288 15.0062 77.0288 12.1049C77.0288 9.17284 75.1188 7.19753 72.3947 7.19753C69.6393 7.19753 67.6353 9.17284 67.6353 12.0741Z" fill="#22242A" />
              <path d="M86.1747 25.5C85.8303 25.5 85.6737 25.3148 85.8302 25.0062L88.0221 19.6667L82.1042 5.03704C81.9789 4.69753 82.1355 4.51235 82.4799 4.51235H85.0162C85.2666 4.51235 85.4545 4.6358 85.5484 4.88272L89.6816 15.8086L93.846 4.88272C93.9399 4.6358 94.1278 4.51235 94.3783 4.51235H96.9145C97.259 4.51235 97.3842 4.69753 97.259 5.03704L89.1806 25.1296C89.0867 25.3765 88.8675 25.5 88.617 25.5H86.1747Z" fill="#22242A" />
              <path d="M98.7022 18.8184C100.518 18.8184 102 20.2682 102 22.0577C102 23.8473 100.518 25.2971 98.7022 25.2971C96.8867 25.2971 95.4045 23.8473 95.4045 22.0577C95.4045 20.2682 96.8867 18.8184 98.7022 18.8184ZM98.7022 24.5156C100.081 24.5156 101.161 23.4169 101.161 22.0577C101.161 20.6873 100.081 19.5886 98.7022 19.5886C97.3234 19.5886 96.2318 20.6873 96.2318 22.0577C96.2318 23.4169 97.3234 24.5156 98.7022 24.5156ZM100.288 21.5481C100.288 22.1144 99.9317 22.4542 99.3572 22.5561L100.253 23.4962C100.299 23.5528 100.276 23.6208 100.196 23.6208H99.5985C99.541 23.6208 99.5066 23.5981 99.4721 23.5641L98.5758 22.5901H98.0013V23.5188C98.0013 23.5755 97.9668 23.6208 97.9094 23.6208H97.4038C97.3464 23.6208 97.3119 23.5755 97.3119 23.5188V20.5853C97.3119 20.5287 97.3464 20.4834 97.4038 20.4834H98.9665C99.7594 20.4834 100.288 20.8345 100.288 21.5481ZM98.978 22.0011C99.3802 22.0011 99.5755 21.8086 99.5755 21.5481C99.5755 21.2649 99.3802 21.0837 98.978 21.0837H98.0013V22.0011H98.978Z" fill="#22242A" />
            </svg>

            <p className='body-large' style={{ marginBottom: '28px' }}>
              {t('Your Vision, Our Solution.')}
            </p>

            <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
              <button className='lang-picker-btn' onClick={handleToggle} style={{ padding: '0.5em 1em' }}>
                {t(languageOptions.find((opt) => opt.value === language)?.label)}
                <svg width="12" height="8" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.5L7 7.5L13 1.5" stroke="#808698" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <ul className={`lang-menu ${isOpen ? 'visible' : ''}`} aria-hidden={!isOpen}>
                {languageOptions.map((type) => (
                  <li key={type.value}>
                    <a
                      href={hrefByLang[type.value] || '#'}
                      onClick={(e) => handleLanguageChange(type.value, e)}
                    >
                      {t(type.label)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* your other footer columns unchanged */}
          <div className='d-flex flex-column'>
            <small>{t('Products')}</small>
            <a href={`${langPrefix}/ai/`}><label>{t('Prostay AI')}</label></a>
            <a href={`${langPrefix}/property-management-system/`}><label>{t('Property Management')}</label></a>
            <a href={`${langPrefix}/channel-manager/`}><label>{t('Channel Manager')}</label></a>
            <a href={`${langPrefix}/booking-engine/`}><label>{t('Booking Engine')}</label></a>
            <a href={`${langPrefix}/prostay-nexus/`}><label>{t('Prostay Nexus')}</label></a>
            <a href={`${langPrefix}/point-of-sale/`}><label>{t('Point of Sale')}</label></a>
            <a href={`${langPrefix}/prostay-pay/`}><label>{t('Prostay Pay')}</label></a>
          </div>

          <div className='d-flex flex-column'>
            <small>{t('Features')}</small>
            <a href={`${langPrefix}/ai/ai-booking-assistant/`}><label>{t('AI Booking Assistant')}</label></a>
            <a href={`${langPrefix}/ai/ai-front-desk-agent/`}><label>{t('AI Front Desk Agent')}</label></a>
            <a href={`${langPrefix}/hotel-sales-software/`}><label>{t('Hotel Sales Software')}</label></a>
          </div>

          <div className='d-flex flex-column'>
            <small>{t('Solutions')}</small>
            <a href={`${langPrefix}/hotel-management-software/`}><label>{t('Hotels')}</label></a>
            <a href={`${langPrefix}/multi-property-groups/`}><label>{t('Multi-Property Groups')}</label></a>
            <a href={`${langPrefix}/vacation-rentals/`}><label>{t('Vacation Rentals')}</label></a>
            <a href={`${langPrefix}/bed-breakfast/`}><label>{t('Bed & Breakfasts')}</label></a>
            <a href={`${langPrefix}/small-hotels/`}><label>{t('Small Hotels')}</label></a>
          </div>

          <div className='d-flex flex-column'>
            <small>{t('Company')}</small>
            <a href={`${langPrefix}/contact-us/`}><label>{t('Contact Us')}</label></a>
            <a href={`${langPrefix}/news/`}><label>{t('News & Press')}</label></a>
            <a href={`${langPrefix}/blog/`}><label>{t('Blog')}</label></a>
            <a href={`${langPrefix}/reviews/`}><label>{t('Reviews')}</label></a>
            <a href={`${langPrefix}/compare/`}><label>{t('Compare')}</label></a>
          </div>

          <div className='d-flex flex-column'>
            <small>{t('Resources')}</small>
            <a href={`${langPrefix}/resources/`}><label>{t('All Resources')}</label></a>
            <a href={`${langPrefix}/product-updates/`}><label>{t('Product Updates')}</label></a>
            <a href={`${langPrefix}/tools/`}><label>{t('Tools')}</label></a>
          </div>
        </div>
      </div>

      <div className='d-flex justify-content-center mb-5'>
        <div className='d-flex page-content gap-2 gap-lg-5'>
          <a href={`${langPrefix}/terms-and-conditions/`}><label>{t('Terms of Service')}</label></a>
          <a href={`${langPrefix}/privacy-policy/`}><label>{t('Policy')}</label></a>
          <a href={`${langPrefix}/cookies-policy/`}><label>{t('Cookies')}</label></a>
          <a href={`${langPrefix}/data-privacy/`}><label>{t('Data Security')}</label></a>
          <a href={`${langPrefix}/privacy-policy/`}><label>{t('Do Not Sell or Share My Personal Information')}</label></a>
        </div>
      </div>

      <div className='footer-bottom d-flex justify-content-center'>
        <div className='page-content d-flex justify-content-between'>
          <small style={{ fontWeight: '400' }}>© 2025 prostay. {t('All rights reserved.')}</small>
          <div className='d-flex justify-content-center align-items-center gap-4'>
            <a href="https://x.com/ProstayPMS" target="_blank" rel="noopener noreferrer">
              <svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M15.9455 22L10.396 14.0901L3.44886 22H0.509766L9.09209 12.2311L0.509766 0H8.05571L13.286 7.45502L19.8393 0H22.7784L14.5943 9.31648L23.4914 22H15.9455ZM19.2185 19.77H17.2398L4.71811 2.23H6.6971L11.7121 9.25316L12.5793 10.4719L19.2185 19.77Z" fill="#B0B4BF" />
              </svg>
            </a>
            <a href="https://linkedin.com/company/prostaypms" target="_blank" rel="noopener noreferrer">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.2234 0H1.77187C0.792187 0 0 0.773438 0 1.72969V22.2656C0 23.2219 0.792187 24 1.77187 24H22.2234C23.2031 24 24 23.2219 24 22.2703V1.72969C24 0.773438 23.2031 0 22.2234 0ZM7.12031 20.4516H3.55781V8.99531H7.12031V20.4516ZM5.33906 7.43438C4.19531 7.43438 3.27188 6.51094 3.27188 5.37187C3.27188 4.23281 4.19531 3.30937 5.33906 3.30937C6.47813 3.30937 7.40156 4.23281 7.40156 5.37187C7.40156 6.50625 6.47813 7.43438 5.33906 7.43438ZM20.4516 20.4516H16.8937V14.8828C16.8937 13.5562 16.8703 11.8453 15.0422 11.8453C13.1906 11.8453 12.9094 13.2937 12.9094 14.7891V20.4516H9.35625V8.99531H12.7687V10.5609H12.8156C13.2891 9.66094 14.4516 8.70938 16.1813 8.70938C19.7859 8.70938 20.4516 11.0813 20.4516 14.1656V20.4516Z" fill="#B0B4BF" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Footer;
